import type { HistoryEventRecord } from '../../domain/types.js';

export interface HistoryTreeDay {
  key: string;
  hours: HistoryTreeHour[];
}

export interface HistoryTreeHour {
  key: string;
  events: HistoryEventRecord[];
}

export interface HistoryTreeWeek {
  key: string;
  days: HistoryTreeDay[];
}

export interface HistoryTreeMonth {
  key: string;
  weeks: HistoryTreeWeek[];
}

export interface HistoryTree {
  months: HistoryTreeMonth[];
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** Browser-local calendar parts. UTC shifts an evening on the 22nd onto the next morning. */
export function localHistoryParts(date: Date): { month: string; week: string; day: string; hour: string } {
  const month = `${date.getFullYear()}-${pad(date.getMonth() + 1)}`;
  const day = `${month}-${pad(date.getDate())}`;
  const monday = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dow = monday.getDay();
  monday.setDate(monday.getDate() - (dow === 0 ? 6 : dow - 1));
  const week = `${monday.getFullYear()}-${pad(monday.getMonth() + 1)}-${pad(monday.getDate())}`;
  return { month, week, day, hour: `${day}T${pad(date.getHours())}:00` };
}

export function groupHistoryByTime(events: HistoryEventRecord[]): HistoryTree {
  const months = new Map<string, Map<string, Map<string, Map<string, HistoryEventRecord[]>>>>();
  for (const event of events) {
    const date = new Date(event.occurredAt);
    if (!Number.isFinite(date.getTime())) continue;
    const parts = localHistoryParts(date);
    const monthKey = parts.month;
    const weekKey = parts.week;
    const dayKey = parts.day;
    const hourKey = parts.hour;
    const month = months.get(monthKey) ?? new Map();
    const week = month.get(weekKey) ?? new Map();
    const day = week.get(dayKey) ?? new Map();
    const hour = day.get(hourKey) ?? [];
    hour.push(event);
    day.set(hourKey, hour);
    week.set(dayKey, day);
    month.set(weekKey, week);
    months.set(monthKey, month);
  }
  return {
    months: [...months.entries()].sort(([a], [b]) => b.localeCompare(a)).map(([key, weeks]) => ({
      key,
      weeks: [...weeks.entries()].sort(([a], [b]) => b.localeCompare(a)).map(([weekKey, days]) => ({
        key: weekKey,
        days: [...days.entries()].sort(([a], [b]) => b.localeCompare(a)).map(([dayKey, dayEvents]) => ({
          key: dayKey,
          hours: [...dayEvents.entries()].sort(([a], [b]) => b.localeCompare(a)).map(([hourKey, hourEvents]) => ({
            key: hourKey,
            events: hourEvents.slice().sort((a, b) => b.occurredAt.localeCompare(a.occurredAt)),
          })),
        })),
      })),
    })),
  };
}

export const buildHistoryTree = groupHistoryByTime;
